import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { BomItem } from '../../../models/bom-item.model';
import { CreateBomItemResponseDto } from '../bom-tree/dtos/create-bom-item-response.dto';
import { ClientOrderDto, ClientOrderHeader, ClientOrderHeaderDto, CreatedClientOrderItemDto } from './models/client-order.dto';
import { Order, OrderItem } from '../orders-new/models/order.dto';
import { ShipmentHeader, ShipmentHeaderDto, ShipmentItemDto } from '../shipment/models/shipment.dto';
import { ShipmentListItemDto } from '../shipments/models/shipment-list-item.dto';

@Injectable({ providedIn: 'root' })
export class ClientOrderProvider {
  constructor(private http: HttpClient) {}

  getClientOrder(clientOrderId: number): Observable<ClientOrderDto> {
    const url = `/api/client-orders/${clientOrderId}`;
    return this.http.get<ClientOrderDto>(url);
  }

  createClientOrder(header: ClientOrderHeader): Observable<ClientOrderHeaderDto> {
    const url = `/api/client-orders`;
    return this.http.post<ClientOrderHeaderDto>(url, header);
  }

  patchClientOrder(clientOrderId: number, header: ClientOrderHeader): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}`;
    return this.http.patch<void>(url, header);
  }

  // confirmation (0016): the one status a person sets; the ship / un-ship twin
  confirmClientOrder(clientOrderId: number): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}/confirm`;
    return this.http.patch<void>(url, {});
  }

  unconfirmClientOrder(clientOrderId: number): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}/unconfirm`;
    return this.http.patch<void>(url, {});
  }

  createClientOrderItem(clientOrderId: number, bomItemId: number, amount: number): Observable<CreatedClientOrderItemDto> {
    const url = `/api/client-orders/${clientOrderId}/items`;
    return this.http.post<CreatedClientOrderItemDto>(url, { bomItemId, amount });
  }

  patchClientOrderItemAmount(clientOrderId: number, itemId: number, amount: number): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}/items/${itemId}/amount`;
    return this.http.patch<void>(url, { amount });
  }

  // grafts the BOM item's subtree under the node; amount is per one parent
  createClientOrderItemChild(clientOrderId: number, itemId: number, bomItemId: number, amount: number): Observable<CreatedClientOrderItemDto> {
    const url = `/api/client-orders/${clientOrderId}/items/${itemId}/children`;
    return this.http.post<CreatedClientOrderItemDto>(url, { bomItemId, amount });
  }

  // removes the node with its whole subtree; on a root, the whole position
  deleteClientOrderItem(clientOrderId: number, itemId: number): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}/items/${itemId}`;
    return this.http.delete<void>(url);
  }

  // unlink: frees the client demand; the production item stays as ad-hoc work
  deleteAllocation(clientOrderId: number, allocationId: number): Observable<void> {
    const url = `/api/client-orders/${clientOrderId}/allocations/${allocationId}`;
    return this.http.delete<void>(url);
  }

  // header-only zlecenie — the positions arrive through createOrderItemFromPosition;
  // a null name keeps the numbered default
  createProductionOrder(name: string | null, deadline: string | null): Observable<Order> {
    const url = `/api/production-orders`;
    return this.http.post<Order>(url, { bomItemId: null, name, deadline });
  }

  // client-sourced item: a copy of the position's tree plus the allocation covering `amount` of it
  createOrderItemFromPosition(orderId: number, clientOrderItemId: number, amount: number): Observable<OrderItem> {
    const url = `/api/production-orders/${orderId}/items`;
    return this.http.post<OrderItem>(url, { clientOrderItemId, amount });
  }

  // shipping (0015): every wysyłka — the page keeps the Planowana ones for "Dodaj do wysyłki"
  getShipments(): Observable<ShipmentListItemDto[]> {
    const url = `/api/shipments`;
    return this.http.get<ShipmentListItemDto[]>(url);
  }

  // header-only wysyłka — the lines arrive through createShipmentItem
  createShipment(header: ShipmentHeader): Observable<ShipmentHeaderDto> {
    const url = `/api/shipments`;
    return this.http.post<ShipmentHeaderDto>(url, header);
  }

  // one line: `amount` pieces of the position on that shipment
  createShipmentItem(shipmentId: number, clientOrderItemId: number, amount: number): Observable<ShipmentItemDto> {
    const url = `/api/shipments/${shipmentId}/items`;
    return this.http.post<ShipmentItemDto>(url, { clientOrderItemId, amount });
  }

  // takes the position off a planned shipment; its amount is free to ship again
  deleteShipmentItem(shipmentId: number, shipmentItemId: number): Observable<void> {
    const url = `/api/shipments/${shipmentId}/items/${shipmentItemId}`;
    return this.http.delete<void>(url);
  }

  // empty term — the dropdown just opened (0099): the first ten products in name order,
  // ready to click; a typed term searches the whole catalogue, the first page of matches (0091)
  searchBomItems(search: string): Observable<BomItem[]> {
    const url = `/api/bom/list`;
    const params: { search?: string, type?: string, pageSize?: number } = search === '' ? { type: 'product', pageSize: 10 } : { search };
    return this.http.get<{ items: BomItem[] }>(url, { params }).pipe(map(list => list.items));
  }

  // JIT BOM: a position the catalog does not know yet is created there first
  createBomItem(name: string): Observable<CreateBomItemResponseDto> {
    const url = `/api/bom`;
    return this.http.post<CreateBomItemResponseDto>(url, { name });
  }
}
